import { QueryClient, UseMutateFunction, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { axiosInstance } from '@/api/axios'
import {
  Marketing,
  GETMarketingResponse,
  PATCHMarketingRequest,
  PATCHMarketingResponse,
  POSTMarketingRequest,
  POSTMarketingResponse,
  POSTMarketingFunnelRequest,
  GETMarketingFunnelsResponse,
  POSTMarketingFunnelResponse,
  POSTMarketingFunnelPeriodRequest,
  POSTMarketingFunnelPeriodResponse,
  GETMarketingOrdersAllocationResponse,
  POSTMarketingOrdersAllocationRequest,
  POSTMarketingOrdersAllocationResponse,
  MarketingTotalResponseMap,
  MarketingTotalType,
  SwitchMarketingFunnelPeriodInputRequest
} from './types'
import { PeriodTypeQuery, PeriodTypeView } from '@/types'
import { REVENUES_BASE_KEY, TOTALS_KEY, createQueryKey, resetGlobalQueryKey, concatQueryKey } from '@/api/query-keys'

export const MARKETING_CHANNELS_KEY = createQueryKey({
  baseKey: REVENUES_BASE_KEY,
  keys: [`marketing-channels`]
})

export const MARKETING_FUNNELS_KEY = MARKETING_CHANNELS_KEY.concat([`funnel`])
export const MARKETING_ORDERS_ALLOCATION_KEY = MARKETING_FUNNELS_KEY.concat([`order-allocation`])

const MARKETING_TOTAL_RESULTS_KEY = MARKETING_CHANNELS_KEY.concat([`total-results`])

export const getMarketings = async (financialModelId: string) => {
  const response = await axiosInstance.get<GETMarketingResponse>(`/marketing-channels?financial_model_id=${financialModelId}`)
  return response.data
}

export const useGetMarketingsSuspenseQuery = (financialModelId: string) =>
  useSuspenseQuery({
    queryKey: MARKETING_CHANNELS_KEY,
    queryFn: () => getMarketings(financialModelId),
    select(data) {
      return data.sort((a, b) => {
        const aCreatedAt = new Date(a.created_at).getTime()
        const bCreatedAt = new Date(b.created_at).getTime()
        return aCreatedAt - bCreatedAt
      })
    }
  })

export const getMarketing = async (id: string) => {
  const response = await axiosInstance.get<Marketing>(`/marketing-channels/${id}`)
  return response.data
}

export const postMarketing = async (marketing: POSTMarketingRequest): Promise<POSTMarketingResponse> => {
  const response = await axiosInstance.post<POSTMarketingResponse>(`/marketing-channels`, marketing)
  return response.data
}

export const useCreateMarketingChannelMutation = (
  funnelMutate: UseMutateFunction<POSTMarketingFunnelResponse, Error, POSTMarketingFunnelRequest, unknown>
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (marketing: POSTMarketingRequest) => postMarketing(marketing),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: MARKETING_CHANNELS_KEY })
      if (funnelMutate) {
        funnelMutate(
          {
            financial_model_id: data.financial_model_id,
            marketing_channel_id: data.id,
            sub_channel_id: null
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: MARKETING_FUNNELS_KEY
              })
            }
          }
        )
      }
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const patchMarketing = async (id: string, marketing: PATCHMarketingRequest): Promise<PATCHMarketingResponse> => {
  const response = await axiosInstance.patch<PATCHMarketingResponse>(`/marketing-channels/${id}`, marketing)
  return response.data
}

export const useUpdateMarketingMutation = (
  id: string,
  funnelMutate?: UseMutateFunction<POSTMarketingFunnelResponse, Error, POSTMarketingFunnelRequest, unknown>
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (marketing: PATCHMarketingRequest) => patchMarketing(id, marketing),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: MARKETING_CHANNELS_KEY })
      resetGlobalQueryKey([TOTALS_KEY])
      if (funnelMutate) {
        data.sub_channels.forEach(subChannel => {
          funnelMutate(
            {
              financial_model_id: data.financial_model_id,
              marketing_channel_id: data.id,
              sub_channel_id: subChannel.id
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: MARKETING_FUNNELS_KEY
                })
              }
            }
          )
        })
      }
    }
  })
}

export const deleteMarketing = async (ids: string[]) => {
  const promises = ids.map(id => axiosInstance.delete(`/marketing-channels/${id}`))

  return Promise.all(promises)
}

export const useDeleteMarketingMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => deleteMarketing(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MARKETING_CHANNELS_KEY })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const getMarketingFunnels = async (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) => {
  const response = await axiosInstance.get<GETMarketingFunnelsResponse>(`/funnels/${financialModelId}/marketing-acquisition-channels`, {
    params: {
      type: period,
      view: view ?? `monthly`
    }
  })

  return response.data
}

export const useGetMarketingFunnelsSuspenseQuery = (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: concatQueryKey(MARKETING_FUNNELS_KEY, [period, view ?? `monthly`]),
    queryFn: () => getMarketingFunnels(financialModelId, period, view)
  })

export const postMarketingFunnel = async (marketingFunnel: POSTMarketingFunnelRequest) => {
  const response = await axiosInstance.post<POSTMarketingFunnelResponse>(
    `/marketing-channels/${marketingFunnel.financial_model_id}/funnel?channel_id=${marketingFunnel.marketing_channel_id}${marketingFunnel.sub_channel_id ? `&sub_channel_id=${marketingFunnel.sub_channel_id}` : ``}`
  )

  return response.data
}

export const useCreateMarketingFunnelMutation = () =>
  useMutation({
    mutationFn: (marketingFunnel: POSTMarketingFunnelRequest) => postMarketingFunnel(marketingFunnel),
    onSuccess: () => {
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })

export const postMarketingFunnelPeriod = async (
  financialModelId: string,
  marketingChannelId: string,
  subChannelId: string | null,
  funnelId: string | null,
  marketingFunnelPeriod: POSTMarketingFunnelPeriodRequest
) => {
  let id = funnelId

  if (!funnelId) {
    const res = await postMarketingFunnel({
      financial_model_id: financialModelId,
      marketing_channel_id: marketingChannelId,
      sub_channel_id: subChannelId
    })
    id = res.funnel_id
  }

  const response = await axiosInstance.post<POSTMarketingFunnelPeriodResponse>(`/funnels/${id}/marketing-acquisition-data`, marketingFunnelPeriod)
  return response.data
}

export const postMarketingFunnelPeriodBulk = async (
  financialModelId: string,
  marketingChannelId: string,
  subChannelId: string | null,
  funnelId: string | null,
  marketingFunnelPeriods: POSTMarketingFunnelPeriodRequest[]
) => {
  let id = funnelId

  if (!funnelId) {
    const res = await postMarketingFunnel({
      financial_model_id: financialModelId,
      marketing_channel_id: marketingChannelId,
      sub_channel_id: subChannelId
    })
    id = res.funnel_id
  }

  const response = await axiosInstance.post(`/funnels/${id}/marketing-acquisition-data/bulk`, {
    periods: marketingFunnelPeriods
  })
  return response.data
}

export const getCreateMarketingFunnelPeriodMutationArgs = (queryClient: QueryClient) => ({
  mutationFn: (props: {
    financialModelId: string
    marketingChannelId: string
    subChannelId: string | null
    funnelId: string | null
    marketingFunnelPeriod: POSTMarketingFunnelPeriodRequest
  }) => postMarketingFunnelPeriod(props.financialModelId, props.marketingChannelId, props.subChannelId, props.funnelId, props.marketingFunnelPeriod),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: MARKETING_FUNNELS_KEY })
    resetGlobalQueryKey([TOTALS_KEY])
  },
  mutationKey: [`marketing-funnels`]
})

export const useCreateMarketingFunnelPeriodMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(getCreateMarketingFunnelPeriodMutationArgs(queryClient))
}

export const getMarketingOrdersAllocation = async (funnelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) => {
  const response = await axiosInstance.get<GETMarketingOrdersAllocationResponse>(`/funnels/${funnelId}/order-allocation`, {
    params: {
      type: period,
      view: view ?? `monthly`
    }
  })
  return {
    funnel_id: response.data.funnel_id,
    customer_segments: response.data.customer_segments.map(customerSegment => ({
      ...customerSegment,
      order_types: customerSegment.order_types.map(orderType => ({
        ...orderType,
        order_allocations: orderType.order_allocations.map(order => ({
          ...order,
          order_percentage: order.order_percentage !== null ? Number(order.order_percentage) : null
        }))
      }))
    }))
  }
}

export const useGetMarketingOrdersAllocationSuspenseQuery = (funnelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: concatQueryKey(MARKETING_ORDERS_ALLOCATION_KEY, [funnelId, period, view ?? `monthly`]),
    queryFn: () => getMarketingOrdersAllocation(funnelId, period, view)
  })

export const postMarketingOrdersAllocation = async (funnelId: string, marketingOrdersAllocation: POSTMarketingOrdersAllocationRequest) => {
  const response = await axiosInstance.post<POSTMarketingOrdersAllocationResponse>(`/funnels/${funnelId}/order-allocation`, marketingOrdersAllocation)
  return response.data
}

export const postMarketingOrdersAllocationBulk = async (funnelId: string, marketingOrdersAllocation: POSTMarketingOrdersAllocationRequest[]) => {
  const response = await axiosInstance.post<POSTMarketingOrdersAllocationResponse>(`/funnels/${funnelId}/order-allocation/bulk`, {
    periods: marketingOrdersAllocation
  })

  return response.data
}

export const getCreateMarketingOrdersAllocationMutationArgs = (queryClient: QueryClient) => ({
  mutationFn: (props: { funnelId: string; marketingOrdersAllocation: POSTMarketingOrdersAllocationRequest }) =>
    postMarketingOrdersAllocation(props.funnelId, props.marketingOrdersAllocation),
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: MARKETING_ORDERS_ALLOCATION_KEY
    })
    resetGlobalQueryKey([TOTALS_KEY])
  }
})

export const useCreateMarketingOrdersAllocationMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(getCreateMarketingOrdersAllocationMutationArgs(queryClient))
}

const switchMarketingFunnelPeriodInput = async (request: SwitchMarketingFunnelPeriodInputRequest) => {
  const response = await axiosInstance.post(
    `/funnels/${request.funnelId}/marketing-config/${request.columnName}`,
    {},
    {
      params: {
        input_type: request.inputType,
        year: request.year,
        month: request.month
      }
    }
  )
  return response.data
}

export const useSwitchMarketingFunnelPeriodInputMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: SwitchMarketingFunnelPeriodInputRequest) => switchMarketingFunnelPeriodInput(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: MARKETING_FUNNELS_KEY
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

const getMarketingTotalResults = async <T extends MarketingTotalType>({
  financialModelId,
  type,
  totalType,
  view
}: {
  financialModelId: string
  type: PeriodTypeQuery
  totalType: T
  view?: PeriodTypeView
}) => {
  const response = await axiosInstance.get<MarketingTotalResponseMap[T]>(`/marketing-channels/${financialModelId}/total-${totalType}`, {
    params: { type, view: view ?? `monthly` }
  })

  return response.data
}

export const useGetMarketingTotalResultsSuspenseQuery = <T extends MarketingTotalType>({
  financialModelId,
  type,
  totalType,
  view
}: {
  financialModelId: string
  type: PeriodTypeQuery
  totalType: T
  view?: PeriodTypeView
}) =>
  useSuspenseQuery({
    queryKey: concatQueryKey(MARKETING_TOTAL_RESULTS_KEY, [type, totalType, view ?? `monthly`]),
    queryFn: () => getMarketingTotalResults({ financialModelId, type, totalType, view })
  })
